import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    FiChevronLeft, FiChevronRight, FiChevronDown, FiChevronUp,
    FiFilter, FiRefreshCw, FiSearch, FiDownload, FiCalendar, FiX, FiMoreVertical
} from 'react-icons/fi';
import { apiFetch, downloadCsv } from '../utils/api';

const ACTION_PALETTE = {
    login:              '#10b981',
    logout:             '#6b7280',
    login_failed:       '#ef4444',
    account_locked:     '#dc2626',
    tenant_created:     '#3b82f6',
    tenant_deleted:     '#ef4444',
    tenant_plan_changed:'#8b5cf6',
    tenant_updated:     '#6366f1',
    message_created:    '#10b981',
    message_updated:    '#06b6d4',
    message_deleted:    '#f87171',
    member_joined:      '#34d399',
    member_removed:     '#fb923c',
    member_role_changed:'#a78bfa',
    profile_updated:    '#60a5fa',
    personal_data_exported: '#f59e0b',
    account_deleted:    '#dc2626',
};

const getActionColor = (action) => ACTION_PALETTE[action] || '#9ca3af';

const ActionBadge = ({ action }) => {
    const color = getActionColor(action);
    return (
        <span className="px-2 py-0.5 rounded text-xs font-mono font-semibold"
            style={{ background: color + '20', color }}>
            {action}
        </span>
    );
};

const Logs = () => {
    const [data,          setData]          = useState(null);
    const [loading,       setLoading]       = useState(true);
    const [page,          setPage]          = useState(1);
    const [actionFilter,  setActionFilter]  = useState('');
    const [tenantFilter,  setTenantFilter]  = useState('');
    const [dateFrom,      setDateFrom]      = useState('');
    const [dateTo,        setDateTo]        = useState('');
    const [expanded,      setExpanded]      = useState(null);
    const [allActions,    setAllActions]    = useState([]);
    const [exporting,     setExporting]     = useState(false);
    const [menuOpen,      setMenuOpen]      = useState(false);
    const menuRef = useRef(null);

    useEffect(() => {
        const handler = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false); };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    const buildParams = useCallback((extra = {}) => {
        const params = new URLSearchParams({ page, limit: 50, ...extra });
        if (actionFilter)      params.set('action',   actionFilter);
        if (tenantFilter.trim()) params.set('tenantId', tenantFilter.trim());
        if (dateFrom)          params.set('from',     dateFrom);
        if (dateTo)            params.set('to',       dateTo);
        return params;
    }, [page, actionFilter, tenantFilter, dateFrom, dateTo]);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const d = await apiFetch(`/superadmin/logs?${buildParams()}`);
            setData(d);
            if (d.actions && allActions.length === 0) {
                setAllActions(Object.values(d.actions).sort());
            }
        } catch (e) { console.error(e); }
        finally { setLoading(false); }
    }, [buildParams, allActions.length]);

    const handleExport = async () => {
        setExporting(true);
        try {
            const params = buildParams({ limit: undefined, page: undefined });
            params.delete('limit'); params.delete('page');
            await downloadCsv(`/superadmin/export/logs?${params}`, 'logs.csv');
        } catch (e) { console.error(e); }
        finally { setExporting(false); }
    };

    const clearFilters = () => {
        setActionFilter(''); setTenantFilter('');
        setDateFrom(''); setDateTo(''); setPage(1);
    };

    useEffect(() => { load(); }, [load]);

    return (
        <div className="p-4 sm:p-6 space-y-6">
            <div className="flex flex-wrap items-center justify-between gap-y-3">
                <div>
                    <h1 className="text-2xl font-bold text-white">Logs d'audit</h1>
                    {data && (
                        <p className="text-gray-500 text-sm mt-0.5">
                            {data.total.toLocaleString('fr-FR')} événement{data.total > 1 ? 's' : ''} au total
                        </p>
                    )}
                </div>
                <div className="relative" ref={menuRef}>
                    <button
                        onClick={() => setMenuOpen(o => !o)}
                        className="p-2 rounded-lg bg-gray-800 hover:bg-gray-700 border border-gray-700 text-gray-400 hover:text-white transition-colors"
                        title="Options"
                    >
                        <FiMoreVertical size={16} aria-hidden="true" />
                    </button>
                    {menuOpen && (
                        <div className="absolute right-0 top-full mt-1 z-20 bg-gray-800 border border-gray-700 rounded-xl shadow-xl overflow-hidden min-w-[160px]">
                            <button onClick={() => { handleExport(); setMenuOpen(false); }} disabled={exporting}
                                className="flex items-center gap-2.5 w-full px-4 py-2.5 text-sm text-gray-300 hover:bg-gray-700 hover:text-white transition-colors disabled:opacity-50">
                                <FiDownload size={13} className={exporting ? 'animate-bounce' : ''} aria-hidden="true" />
                                Exporter CSV
                            </button>
                            <button onClick={() => { load(); setMenuOpen(false); }} disabled={loading}
                                className="flex items-center gap-2.5 w-full px-4 py-2.5 text-sm text-gray-300 hover:bg-gray-700 hover:text-white transition-colors disabled:opacity-50">
                                <FiRefreshCw size={13} className={loading ? 'animate-spin' : ''} aria-hidden="true" />
                                Actualiser
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {/* Filtres */}
            <div className="flex flex-wrap gap-3 items-center bg-gray-900 border border-gray-800 rounded-xl p-3">
                <FiFilter size={14} className="text-gray-500" aria-hidden="true" />
                <select
                    value={actionFilter}
                    onChange={e => { setActionFilter(e.target.value); setPage(1); }}
                    className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-1.5 text-gray-300 text-sm focus:outline-none focus:border-brand-500 min-w-[160px]"
                >
                    <option value="">Toutes les actions</option>
                    {allActions.map(a => (
                        <option key={a} value={a}>{a}</option>
                    ))}
                </select>
                <div className="relative">
                    <FiSearch size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-500" aria-hidden="true" />
                    <input
                        type="text"
                        placeholder="Org ID..."
                        value={tenantFilter}
                        onChange={e => { setTenantFilter(e.target.value); setPage(1); }}
                        className="bg-gray-800 border border-gray-700 rounded-lg pl-8 pr-3 py-1.5 text-gray-300 text-sm focus:outline-none focus:border-brand-500 w-44 font-mono"
                    />
                </div>
                {/* Plage de dates */}
                <div className="flex items-center gap-1.5 bg-gray-800 border border-gray-700 rounded-lg p-2">
                    <FiCalendar size={13} className="text-gray-500 shrink-0" aria-hidden="true" />
                    <input
                        type="date"
                        value={dateFrom}
                        onChange={e => { setDateFrom(e.target.value); setPage(1); }}
                        className="bg-transparent text-gray-300 text-sm focus:outline-none w-32"
                        title="Date de début"
                    />
                    <span className="text-gray-600 text-xs">→</span>
                    <input
                        type="date"
                        value={dateTo}
                        onChange={e => { setDateTo(e.target.value); setPage(1); }}
                        className="bg-transparent text-gray-300 text-sm focus:outline-none w-32"
                        title="Date de fin"
                    />
                </div>
                {(actionFilter || tenantFilter || dateFrom || dateTo) && (
                    <button onClick={clearFilters}
                        className="flex items-center gap-1 text-gray-500 hover:text-gray-300 text-xs transition-colors">
                        <FiX size={12} aria-hidden="true" /> Effacer
                    </button>
                )}
            </div>

            {loading ? (
                <div className="flex justify-center py-16">
                    <div className="w-8 h-8 border-2 border-brand-500 border-t-transparent rounded-full animate-spin" />
                </div>
            ) : (
                <div className="space-y-1.5">
                    {data?.logs?.map(log => (
                        <div key={log._id} className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
                            <div
                                className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-gray-800/50 transition-colors flex-wrap"
                                onClick={() => setExpanded(expanded === log._id ? null : log._id)}
                            >
                                {/* Dot temporel */}
                                <div className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: getActionColor(log.action) }} />

                                <ActionBadge action={log.action} />

                                <div className="flex items-center gap-2 min-w-0 flex-1">
                                    <span className="text-white text-sm font-medium truncate">{log.actorName}</span>
                                    <span className="text-gray-500 text-xs truncate hidden sm:block">{log.actorEmail}</span>
                                </div>

                                {log.tenantName && (
                                    <span className="text-xs bg-gray-800 text-gray-400 px-2 py-0.5 rounded-lg font-medium shrink-0">
                                        {log.tenantName}
                                    </span>
                                )}

                                <span className="text-gray-500 text-xs shrink-0 ml-auto">
                                    {new Date(log.createdAt).toLocaleString('fr-FR', { day:'2-digit', month:'short', hour:'2-digit', minute:'2-digit' })}
                                </span>

                                {expanded === log._id
                                    ? <FiChevronUp size={14} className="text-gray-600 shrink-0" aria-hidden="true" />
                                    : <FiChevronDown size={14} className="text-gray-600 shrink-0" aria-hidden="true" />
                                }
                            </div>

                            {/* Détails */}
                            {expanded === log._id && (
                                <div className="border-t border-gray-800 p-4 bg-gray-950/50">
                                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 text-sm">
                                        {[
                                            ['Cible', log.targetName || '—'],
                                            ['IP', log.ip || '—'],
                                            ['Org ID', log.tenantId],
                                            ['Actor ID', log.actorId],
                                        ].map(([k, v]) => (
                                            <div key={k} className="bg-gray-900 rounded-lg p-3">
                                                <div className="text-gray-500 text-xs mb-1">{k}</div>
                                                <div className="text-white font-mono text-xs break-all">{String(v)}</div>
                                            </div>
                                        ))}
                                    </div>

                                    {log.metadata && Object.keys(log.metadata).length > 0 && (
                                        <div className="mt-3 bg-gray-900 rounded-lg p-3">
                                            <div className="text-gray-500 text-xs mb-2">Metadata</div>
                                            <pre className="text-xs font-mono text-gray-300 whitespace-pre-wrap overflow-auto max-h-40">
                                                {JSON.stringify(log.metadata, null, 2)}
                                            </pre>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    ))}

                    {data?.logs?.length === 0 && (
                        <div className="text-center py-12 text-gray-600">
                            Aucun log trouvé pour ces filtres
                        </div>
                    )}
                </div>
            )}

            {/* Pagination */}
            {data && data.pages > 1 && (
                <div className="flex items-center justify-center gap-3 pt-2">
                    <button onClick={() => setPage(p => p - 1)} disabled={page <= 1}
                        aria-label="Page précédente"
                        className="p-2 rounded-lg border border-gray-700 text-gray-400 hover:text-white hover:bg-gray-800 disabled:opacity-30 transition-colors">
                        <FiChevronLeft size={16} aria-hidden="true" />
                    </button>
                    <span className="text-gray-400 text-sm">{page} / {data.pages}</span>
                    <button onClick={() => setPage(p => p + 1)} disabled={page >= data.pages}
                        aria-label="Page suivante"
                        className="p-2 rounded-lg border border-gray-700 text-gray-400 hover:text-white hover:bg-gray-800 disabled:opacity-30 transition-colors">
                        <FiChevronRight size={16} aria-hidden="true" />
                    </button>
                </div>
            )}
        </div>
    );
};

export default Logs;
